import { useCallback, useState } from "react";

// A form draft for one section. The draft starts as the server's values. It is "dirty" while it differs
// from them. When the server's values change (a save elsewhere on the page, a refetch after a conflict):
//   - a clean draft takes the new values;
//   - a dirty draft KEEPS the fields the staff member changed and takes the server's new value for every
//     field they did not touch (a three-way merge by field). Without that merge, a stale value in a field
//     the staff member never touched would be saved on top of somebody else's change.
//   - a dirty draft that is a list (categories, images) cannot be merged by field: it keeps the whole list.
// After a successful save the section calls `reset` with the values of the answer, so the draft matches
// the server and is clean even when the server normalized a value (it trimmed a space).

type DraftState<T> = Readonly<{ base: string; baseValue: T; values: T }>;

const isRecord = (value: unknown): value is Readonly<Record<string, unknown>> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

/** The draft after the server's values moved from `oldBase` to `next`. */
export function mergeDraft<T>(oldBase: T, values: T, next: T): T {
  if (same(values, oldBase)) return next;
  if (!isRecord(oldBase) || !isRecord(values) || !isRecord(next)) return values;
  const merged: Record<string, unknown> = { ...next };
  for (const key of Object.keys(values)) {
    if (!same(values[key], oldBase[key])) merged[key] = values[key];
  }
  return merged as T;
}

export function useDraft<T>(server: T) {
  const serverKey = JSON.stringify(server);
  const [state, setState] = useState<DraftState<T>>({
    base: serverKey,
    baseValue: server,
    values: server,
  });

  if (state.base !== serverKey) {
    // Derived state: React runs this render again at once with the new state.
    setState({
      base: serverKey,
      baseValue: server,
      values: mergeDraft(state.baseValue, state.values, server),
    });
  }

  const dirty = JSON.stringify(state.values) !== serverKey;
  const setValues = useCallback(
    (next: T | ((current: T) => T)) =>
      setState((current) => ({
        ...current,
        values: typeof next === "function" ? (next as (current: T) => T)(current.values) : next,
      })),
    [],
  );
  const reset = useCallback(
    (values: T) => setState({ base: JSON.stringify(values), baseValue: values, values }),
    [],
  );
  const discard = useCallback(
    () => setState((current) => ({ ...current, values: current.baseValue })),
    [],
  );

  return { values: state.values, setValues, dirty, reset, discard };
}
